import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Button,
  Spin,
  Typography,
  Space,
} from 'antd';
import {
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Text } = Typography;

interface ImageData {
  id: number;
  bucket_name: string;
  region: string;
  s3_url: string;
  thumbnail_uri: string;
  original_width: number;
  original_height: number;
  thumbnail_width: number;
  thumbnail_height: number;
  file_size_formatted: string;
  size_category: string;
  caption_summary?: string;
  rotation?: number;
}

interface FullscreenViewerProps {
  visible: boolean;
  image: ImageData | null;
  onClose: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  hasNext?: boolean;
  hasPrev?: boolean;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

const FullscreenViewer: React.FC<FullscreenViewerProps> = ({
  visible,
  image,
  onClose,
  onNext,
  onPrev,
  hasNext = false,
  hasPrev = false,
}) => {
  const [captionData, setCaptionData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [captionPanelVisible, setCaptionPanelVisible] = useState(true);
  // Measure the image area so a saved 90/270 rotation can be scaled to fit.
  const imageBoxRef = useRef<HTMLDivElement>(null);
  const [imageBoxSize, setImageBoxSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  useEffect(() => {
    const el = imageBoxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => setImageBoxSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const rotation = image?.rotation || 0;
  const isQuarterTurn = rotation === 90 || rotation === 270;
  const rotatedFit = isQuarterTurn && imageBoxSize.w > 0 && imageBoxSize.h > 0;

  const fetchCaption = useCallback(async (imageId: number) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/images/${imageId}/caption-json`);
      setCaptionData(response.data.caption_json);
    } catch (err) {
      // Image may not have captions yet
      setCaptionData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (visible && image) {
      fetchCaption(image.id);
    } else {
      setCaptionData(null);
    }
  }, [visible, image, fetchCaption]);

  // Keyboard navigation
  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && onPrev && hasPrev) {
        onPrev();
      } else if (e.key === 'ArrowRight' && onNext && hasNext) {
        onNext();
      } else if (e.key === ' ') {
        e.preventDefault();
        setCaptionPanelVisible(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, onClose, onNext, onPrev, hasNext, hasPrev]);

  if (!image) return null;

  const JSON_PANEL_WIDTH = 450;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      closable={false}
      width="100vw"
      style={{ top: 0, padding: 0, maxWidth: '100vw' }}
      styles={{
        body: { padding: 0, height: '100vh', overflow: 'hidden' },
      }}
      className="fullscreen-viewer-modal"
      destroyOnClose
    >
      <div style={{
        display: 'flex',
        height: '100vh',
        backgroundColor: '#000',
      }}>
        {/* Header Bar */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 48,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 16px',
          zIndex: 100,
        }}>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onClose}
            style={{ color: '#fff' }}
          >
            Close
          </Button>

          <Space>
            <Text style={{ color: '#fff' }}>
              ID: {image.id} | {image.original_width}×{image.original_height}
            </Text>
          </Space>

          <Space>
            <Button
              type="text"
              icon={captionPanelVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={() => setCaptionPanelVisible(prev => !prev)}
              style={{ color: '#fff' }}
            >
              {captionPanelVisible ? 'Hide' : 'Show'} Caption
            </Button>
            <Button
              type="text"
              icon={<LeftOutlined />}
              onClick={onPrev}
              disabled={!hasPrev}
              style={{ color: hasPrev ? '#fff' : '#666' }}
            />
            <Button
              type="text"
              icon={<RightOutlined />}
              onClick={onNext}
              disabled={!hasNext}
              style={{ color: hasNext ? '#fff' : '#666' }}
            />
          </Space>
        </div>

        {/* Image Area */}
        <div
          ref={imageBoxRef}
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingTop: 48,
            minWidth: 0,
            height: 'calc(100vh - 48px)',
            overflow: 'hidden',
          }}
        >
          <img
            src={image.s3_url}
            alt={`ID ${image.id}`}
            style={{
              maxWidth: rotatedFit ? imageBoxSize.h : '100%',
              maxHeight: rotatedFit ? imageBoxSize.w : '100%',
              objectFit: 'contain',
              transform: rotation ? `rotate(${rotation}deg)` : undefined,
              transition: 'transform 0.2s ease',
            }}
          />
        </div>

        {/* JSON Panel */}
        {captionPanelVisible && (
          <div style={{
            width: JSON_PANEL_WIDTH,
            backgroundColor: '#1e1e1e',
            borderLeft: '1px solid #333',
            display: 'flex',
            flexDirection: 'column',
            paddingTop: 48,
            flexShrink: 0,
            height: '100vh',
          }}>
            {/* Image Info Header */}
            <div style={{
              padding: '8px 12px',
              borderBottom: '1px solid #333',
              backgroundColor: '#252526',
            }}>
              <Space size={8}>
                <img
                  src={image.thumbnail_uri || image.s3_url}
                  alt={`ID ${image.id}`}
                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                />
                <div>
                  <Text strong style={{ color: '#fff' }}>ID: {image.id}</Text>
                  <br />
                  <Text style={{ fontSize: 11, color: '#888' }}>
                    {image.original_width}×{image.original_height}
                  </Text>
                </div>
              </Space>
            </div>

            {/* JSON Content */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Spin />
                </div>
              ) : captionData ? (
                <pre style={{
                  margin: 0,
                  padding: 12,
                  fontSize: 11,
                  fontFamily: 'Monaco, Menlo, monospace',
                  backgroundColor: '#1e1e1e',
                  color: '#d4d4d4',
                  overflow: 'auto',
                  height: '100%',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                  {JSON.stringify(captionData, null, 2)}
                </pre>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Text style={{ color: '#888' }}>No JSON data available</Text>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default FullscreenViewer;
